import React, { FC, ReactNode, createContext, memo, useContext, useState, useMemo, useEffect } from 'react';
import { RepairFacilities, RepairFacility, RepairFacilitiesResult } from '@components/service-and-repair/services/repair-facilities-service';
import { AutocompleteAddress, UserProfile } from '../../services/user-profile-service';
import { useUserSelections } from '../user-selections';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import { SarStore } from '@store/sar-store';

type RepairFacilitiesContextType = {
  isLoading: boolean;
  userAddress: AutocompleteAddress | null;
  repairFacilities: RepairFacility[];
  experimentName: string | null;
  bucket: string | null;
  fetchRepairFacilities: (userEnteredAddress?: AutocompleteAddress, overrideAsrId?: number | null) => void;
};

export const RepairFacilitiesContext = createContext<RepairFacilitiesContextType>({
  isLoading: false,
  userAddress: null,
  repairFacilities: [],
  experimentName: null,
  bucket: null,
  fetchRepairFacilities: () => {},
});

export const useRepairFacilities = () => {
  const context = useContext(RepairFacilitiesContext);
  if (context === undefined) {
    throw new Error('useRepairFacilities must be used within a RepairFacilitiesProvider');
  }

  return context;
};

type RepairFacilitiesProviderProps = {
  children: ReactNode | ReactNode[];
};

export const RepairFacilitiesProvider: FC<RepairFacilitiesProviderProps> = memo(
  ({ children }: RepairFacilitiesProviderProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userAddress, setUserAddress] = useState<AutocompleteAddress | null>(null);
    const [repairFacilities, setRepairFacilities] = useState<RepairFacility[]>([]);
    const [experimentName, setExperimentName] = useState<string | null>(null);
    const [bucket, setBucket] = useState<string | null>(null);
    const { userSelections, asrId } = useUserSelections();

    const fetchRepairFacilities = async (userEnteredAddress?: AutocompleteAddress, overrideAsrId?: number | null) => {
      setIsLoading(true);

      const currentAsrId = overrideAsrId !== undefined ? overrideAsrId : asrId;

      try {
        const result = await RepairFacilities.fetchFacilities(userEnteredAddress || userAddress, userSelections, currentAsrId);
        const facilities = result?.facilities || [];
        setRepairFacilities(facilities);
        setExperimentName(result?.experimentName || null);
        setBucket(result?.bucket || null);

        const analyticsProps = {
          count: facilities.length,
          variant: 2,
          facilities,
          asrId: currentAsrId ?? null,
          coverageScreen: SarStore.coverageScreenType || null,
          userId: getUserState()?.user?.user_id || null,
          sarSessionId: SarStore.sarSessionId || null,
          experimentName: result?.experimentName || null,
          bucket: result?.bucket || null,
        };

        Analytics.trackEvent(AnalyticEvents.sarCoverageScreenRepairFacilitiesReturned, analyticsProps);
        datadogRum.addAction(AnalyticEvents.sarCoverageScreenRepairFacilitiesReturned, analyticsProps);
      } catch (error) {
        datadogRum.addAction(AnalyticEvents.sarFetchRepairFacilitiesProviderError, {
          type: 'error',
          error: error instanceof Error ? { name: error.name, message: error.message } : String(error),
          sarSessionId: SarStore.sarSessionId,
          asrId: currentAsrId ?? null,
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchUserAddress = async () => {
      try {
        const address = await UserProfile.fetchAddress();
        setUserAddress(address);
      } catch (error) {
        datadogRum.addAction(AnalyticEvents.sarFetchUserAddressProviderError, {
          type: 'error',
          error: error instanceof Error ? { name: error.name, message: error.message } : String(error),
          sarSessionId: SarStore.sarSessionId,
        });
      }
    };

    useEffect(() => {
      fetchUserAddress();
    }, []);

    const value = useMemo(() => ({ isLoading, userAddress, repairFacilities, experimentName, bucket, fetchRepairFacilities }), [
      isLoading,
      userAddress,
      repairFacilities,
      experimentName,
      bucket,
      userSelections,
    ]);

    return <RepairFacilitiesContext.Provider value={value}>{children}</RepairFacilitiesContext.Provider>;
  }
);
